import { Link } from "react-router-dom";
import { SignInButton, SignedIn, SignedOut } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  FileText,
  Download,
  Clock,
  CheckCircle2,
  Users,
} from "lucide-react";

const STEPS = [
  {
    number: "01",
    icon: FileText,
    title: "Enter student info",
    desc: "Fill in grade, performance level, attendance, and a few behavioural indicators. Takes under a minute per student.",
  },
  {
    number: "02",
    icon: Sparkles,
    title: "AI writes the comment",
    desc: "Our model generates a personalized, curriculum-aligned comment that reads like you wrote it - not a robot.",
  },
  {
    number: "03",
    icon: Download,
    title: "Export the PDF",
    desc: "Download your completed Ontario report card, ready to submit. No formatting, no copy-pasting.",
  },
];

const FEATURES = [
  {
    icon: CheckCircle2,
    title: "Ontario-aligned templates",
    desc: "Built specifically for Ontario Progress Reports (Gr 1–6) and Provincial Report Cards (Gr 1–8). No adapting generic templates.",
  },
  {
    icon: Sparkles,
    title: "Comments that sound like you",
    desc: "The AI uses your student's actual data - grades, absences, participation - to write comments that feel specific and authentic.",
  },
  {
    icon: Clock,
    title: "From hours to minutes",
    desc: "Teachers report spending several hours per reporting period. EasyReports brings that down to just minutes.",
  },
  {
    icon: Users,
    title: "Class management built in",
    desc: "Organize students by class, share classes with colleagues, and track usage across your whole reporting cycle.",
  },
];

import { PLANS } from "@/constants/plans";

export default function Home() {
  return (
    <div className="home-root">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-inner">
          <div className="hero-text">
            <span className="hero-eyebrow">For Ontario teachers</span>
            <h1 className="hero-heading">
              Built for Teachers,<br/>
              <em>by a Teacher</em>
            </h1>
            <p className="hero-body">
              EasyReports makes the reportcard making process a breeze. Teachers
              spend hours writing report card comments for each student in seconds - based on your feedback as well as the student&apos;s
              actual grades, attendance, and participation.
            </p>
            <div className="hero-ctas">
              <Link to="/free-trial">
                <button className="cta-primary">
                  Try it free - no account needed
                  <ArrowRight size={16} />
                </button>
              </Link>
              <SignedOut>
                <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                  <button className="cta-secondary">Create free account</button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <Link to="/dashboard">
                  <button className="cta-secondary">Go to dashboard</button>
                </Link>
              </SignedIn>
            </div>
            <p className="hero-trust">
              No credit card required &nbsp;·&nbsp; Free tier always available
            </p>
          </div>

          {/* Demo card */}
          <div className="hero-visual">
            <div className="demo-card">
              <div className="demo-card-header">
                <div>
                  <div className="demo-student-name">Mary R.</div>
                  <div className="demo-student-meta">Grade 3 · Mathematics · Term 1</div>
                </div>
                <span className="demo-badge">
                  <Sparkles size={11} />
                  AI
                </span>
              </div>
              <div className="demo-metrics">
                <span className="demo-metric">Overall: B+</span>
                <span className="demo-metric">Absences: 2</span>
                <span className="demo-metric">Participation: ✓</span>
              </div>
              <div className="demo-comment-block">
                <div className="demo-comment-label">Generated comment</div>
                <p className="demo-comment-text">
                  &ldquo;Mary demonstrates a strong understanding of algebraic rules
                  and consistently applies mathematical strategies when
                  problem-solving. She articulates her reasoning clearly and
                  contributes thoughtfully during class discussions. Mary would
                  benefit from continued practice with multi-step word
                  problems.&rdquo;
                </p>
              </div>
              <div className="demo-card-footer">
                <span className="demo-footer-action">
                  <Download size={13} /> Export PDF
                </span>
                <span className="demo-footer-chars">247 characters</span>
              </div>
            </div>
            <div className="demo-card-glow" />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section className="section-alt">
        <div className="section-inner">
          <div className="section-label">How it works</div>
          <h2 className="section-heading">Three steps, zero stress.</h2>
          <div className="steps-grid">
            {STEPS.map((step) => (
              <div key={step.number} className="step-card">
                <div className="step-number">{step.number}</div>
                <div className="step-icon-wrap">
                  <step.icon size={20} />
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────── */}
      <section className="section-base">
        <div className="section-inner">
          <div className="section-label">Why EasyReports</div>
          <h2 className="section-heading">Built for how teachers actually work.</h2>
          <div className="features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">
                  <f.icon size={20} />
                </div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING TEASER ───────────────────────────────── */}
      <section className="section-dark">
        <div className="section-inner">
          <div className="section-label light">Pricing</div>
          <h2 className="section-heading light">
            Start free. Upgrade when you&apos;re ready.
          </h2>
          <div className="plans-grid">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`plan-card${plan.highlight ? " highlighted" : ""}`}
              >
                {plan.highlight && (
                  <div className="plan-popular">Most popular</div>
                )}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  {plan.price}
                  <span>/yr</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((f) => (
                    <li key={f}>
                      <CheckCircle2 size={13} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <Link to="/pricing">
              <button className="cta-secondary light">
                See full pricing details <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <section className="section-base">
        <div className="final-cta-inner">
          <h2 className="final-cta-heading">
            Ready to reclaim your evenings?
          </h2>
          <p className="final-cta-body">
            Try a free report card right now - no account, no credit card, no
            commitment. 
          </p>
          <div className="hero-ctas" style={{ justifyContent: "center" }}>
            <Link to="/free-trial">
              <button className="cta-primary">
                Try it free now
                <ArrowRight size={16} />
              </button>
            </Link>
            <SignedOut>
              <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                <button className="cta-secondary">Create free account</button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </section>

    </div>
  );
}
